import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

/**
 * THE TANK COLUMN — the hero of the auth screen, and the only ornament in it.
 *
 * This is the fuel column from the Tankly mark, taken out of the logo and drawn at
 * full height. The liquid is a sine wave clipped to the tank's interior, translating
 * exactly one full period so the loop is seamless — you cannot see where it restarts.
 * That is the same technique, and the same numbers, as `website/src/components/Logo.astro`.
 * It is the brand asset, not a reinterpretation of it.
 *
 * WHY THIS AND NOT A GRADIENT
 *
 * The first thing anybody ever sees of Tankly should be the exact thing Tankly is for:
 * a tank, low, with a real site name and a real litre count beside it. Not an
 * illustration of a refinery, not a photograph of a truck, not a mesh gradient. The
 * product's entire proposition is "you will know before it runs out" — so the sign-in
 * page shows a tank that is about to.
 *
 * It fills from empty to its true level once, on load, and then it just breathes. The
 * fill is the page-load moment; the wave is the ambient one. Under
 * prefers-reduced-motion both stop and it renders as a still, correct instrument.
 */

export interface TankColumnProps {
  /** 0–100. The level the liquid settles at. */
  percent: number;
  /** Below this, the fuel turns to the DRY tone — the only warm colour in the product. */
  lowThreshold?: number;
  className?: string;
}

// The tank interior, lifted verbatim from the mark. Everything below is drawn in this
// coordinate space so the curvature of the base is the logo's curvature, not an
// approximation of it.
const VB_W = 181; // 79.42 → 260.42
// The logo's tank is squat. A sight glass is not: it is a tall, narrow column, and the
// height is what gives a level its precision. Drawn at 181 × 620 it reads as an
// instrument you could take a reading off, rather than a jerrycan.
const VB_H = 620;

// Half-period 90.5, so a full period is 181 — exactly the distance the wave travels.
// Amplitude ~9. Six half-periods gives us three screens of wave to slide through one.
const WAVE_CREST = 30; // where the path draws its own surface, in its own space
const WAVE_D = [
  "M0 30",
  "c30.17 -11.52 60.33 -11.52 90.5 0",
  "c30.17 11.52 60.33 11.52 90.5 0",
  "c30.17 -11.52 60.33 -11.52 90.5 0",
  "c30.17 11.52 60.33 11.52 90.5 0",
  "c30.17 -11.52 60.33 -11.52 90.5 0",
  "c30.17 11.52 60.33 11.52 90.5 0",
  `V${VB_H + 40}H0Z`,
].join(" ");

export function TankColumn({ percent, lowThreshold = 20, className }: TankColumnProps) {
  const target = Math.min(100, Math.max(0, percent));
  const dry = target <= lowThreshold;

  // Fill from empty on mount. This is the one orchestrated moment on the page: the
  // tank fills, and while it does, the reader understands what they are looking at
  // without being told.
  const [level, setLevel] = useState(0);
  useEffect(() => {
    const t = window.setTimeout(() => setLevel(target), 220);
    return () => window.clearTimeout(t);
  }, [target]);

  // The liquid's surface, in viewBox units, measured from the top of the tank.
  const surfaceY = VB_H - (VB_H * level) / 100;

  // The wave path draws its own crest at y = WAVE_CREST inside its own coordinate space,
  // so the group has to be lifted by that much for the crest to LAND on the surface.
  // Translating by surfaceY alone drops the whole body of liquid one crest-height too
  // low — at 14% that pushed almost all of it past the clip and the tank read as empty.
  const lift = surfaceY - WAVE_CREST;

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      // `none` lets the column fill its box on BOTH axes, so the glass runs the full
      // height of the panel and off the top of it. The walls are straight, so
      // non-uniform scaling costs nothing: the only curve is the 22-unit floor, and a
      // slightly elliptical floor on a tank is indistinguishable from a round one.
      // `meet` would letterbox it into a stubby object floating mid-panel, which is
      // exactly what it did.
      preserveAspectRatio="none"
      className={cn("h-full w-full", className)}
      aria-hidden="true"
    >
      <defs>
        {/* The tank's interior: straight walls, and the logo's rounded floor. */}
        <clipPath id="tk-interior">
          <path
            d={`M0 0 V${VB_H - 22} C0 ${VB_H - 10} 6 ${VB_H} 16 ${VB_H}
                H165 C175 ${VB_H} ${VB_W} ${VB_H - 10} ${VB_W} ${VB_H - 22} V0 Z`}
          />
        </clipPath>
      </defs>

      <g clipPath="url(#tk-interior)">
        {/* The empty part of the tank — a shadow, not a colour. */}
        <rect x="0" y="0" width={VB_W} height={VB_H} fill="rgba(0,0,0,0.24)" />

        {/* The fuel. Two waves, offset and at different speeds, so the surface reads
            as liquid with weight rather than as a single sliding sine. */}
        <g
          style={{
            transform: `translateY(${lift}px)`,
            transition: "transform 1400ms cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          <path
            className="tk-wave"
            d={WAVE_D}
            fill={dry ? "var(--color-dry)" : "var(--color-cream)"}
            opacity="0.35"
            style={{ animationDuration: "13s" }}
          />
          <path
            className="tk-wave"
            d={WAVE_D}
            fill={dry ? "var(--color-dry)" : "var(--color-cream)"}
            style={{ animationDuration: "9s" }}
          />
        </g>
      </g>

      {/* The glass itself. A hairline, and graduations every 20% — an instrument face,
          so the level can be read without reading the number. */}
      <path
        d={`M0 0 V${VB_H - 22} C0 ${VB_H - 10} 6 ${VB_H} 16 ${VB_H}
            H165 C175 ${VB_H} ${VB_W} ${VB_H - 10} ${VB_W} ${VB_H - 22} V0`}
        fill="none"
        stroke="var(--color-rule-hi)"
        strokeWidth="1.5"
      />
      {[20, 40, 60, 80].map((g) => (
        <line
          key={g}
          x1={VB_W - 26}
          x2={VB_W - 6}
          y1={VB_H - (VB_H * g) / 100}
          y2={VB_H - (VB_H * g) / 100}
          stroke="var(--color-mauve)"
          strokeWidth="1"
        />
      ))}
    </svg>
  );
}
